import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;
  const relatedArticles = id ? getRelatedArticles(id) : [];

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 黑料视频`;
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) metaDescription.setAttribute('content', article.excerpt);
    }
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-heading font-bold text-foreground-900">文章未找到</h2>
          <Link to="/" className="mt-4 inline-block text-primary-600 hover:underline">返回首页</Link>
        </div>
      </div>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '黑料视频',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <section className="relative w-full h-[400px] md:h-[500px] overflow-hidden mx-auto mt-6 md:mt-8" style={{ maxWidth: 'calc(100% - 48px)' }}>
          <div className="absolute inset-0 rounded-[28px] overflow-hidden">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/70 rounded-[28px]"></div>
          </div>
          <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-6 w-full">
            <span className="px-4 py-1.5 rounded-full border border-white/40 text-white text-sm font-medium mb-4 backdrop-blur-sm whitespace-nowrap">
              {article.category}
            </span>
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-white max-w-3xl leading-tight">
              {article.title}
            </h1>
          </div>
        </section>

        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: article.category, path: `/category/${article.categorySlug}` },
            { label: article.title },
          ]}
        />

        <main className="max-w-[900px] mx-auto px-4 md:px-6 py-8 md:py-12">
          <div className="flex items-center gap-4 mb-8 pb-6 border-b border-background-200/70 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                <i className="ri-user-line"></i>
              </span>
              <div>
                <p className="text-sm font-medium text-foreground-900">{article.author}</p>
                <p className="text-xs text-foreground-500">{article.date}</p>
              </div>
            </div>
            <div className="flex items-center gap-1 text-sm text-foreground-500">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-eye-line text-xs"></i>
              </span>
              {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万次阅读` : `${article.views}次阅读`}
            </div>
            <div className="flex flex-wrap gap-1.5 ml-auto">
              {article.tags.map((tag) => (
                <span key={tag} className="px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full whitespace-nowrap">
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          <article className="prose-custom">
            <div
              className="text-foreground-800 text-base leading-relaxed space-y-5"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </article>

          <div className="mt-10 p-6 bg-background-100 rounded-lg border border-background-200/70">
            <p className="text-sm text-foreground-600">
              <strong className="text-foreground-900">免责声明：</strong>
              本文内容来源于网络公开信息及网友投稿，不代表本平台立场。如涉及侵权或不实信息，请联系我们进行删除处理。
            </p>
          </div>
        </main>

        {relatedArticles.length > 0 && (
          <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="mb-6 md:mb-8">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                <a href="#related-articles">相关推荐</a>
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6" data-product-shop="">
              {relatedArticles.map((rel) => (
                <Link
                  key={rel.id}
                  to={`/article/${rel.id}`}
                  className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-accent-300/60 transition-all duration-300"
                >
                  <div className="aspect-video overflow-hidden bg-background-200">
                    <img
                      src={rel.imageUrl}
                      alt={rel.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                      {rel.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-2 text-xs text-foreground-500">
                      <span>{rel.date}</span>
                      <span className="flex items-center gap-1">
                        <span className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-eye-line text-xs"></i>
                        </span>
                        {rel.views > 10000 ? `${(rel.views / 10000).toFixed(1)}万` : rel.views}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <Footer />
      </div>
    </>
  );
}